import { applyOperation, emptyDataset, type Dataset, type SyncOperation } from "@otrack/domain";

export { SyncEngine, SyncRequestError, initialSyncStatus, type SyncApi, type SyncPhase, type SyncStatus } from "./sync-engine";

/** Device-local persistence for the Dataset plus the outbox of operations waiting to sync. */
export interface LocalStore {
  initialize(): Promise<void>;
  close(): Promise<void>;
  getDeviceId(): Promise<string>;
  load(): Promise<Dataset>;
  /** Applies operations, persists the affected entities, and queues the accepted ones for sync. */
  apply(operations: readonly SyncOperation[]): Promise<Dataset>;
  /** Notifies when the Dataset changes, including changes made in another tab or window. */
  subscribe(listener: (dataset: Dataset) => void): () => void;
  /** Applies operations received from the server. They are not queued for sync again. */
  applyRemote(operations: readonly SyncOperation[]): Promise<Dataset>;
  /** Server log position this device has pulled up to. */
  getCursor(): Promise<number>;
  setCursor(cursor: number): Promise<void>;
  getOutbox(limit?: number): Promise<SyncOperation[]>;
  getPendingOperationCount(): Promise<number>;
  acknowledgeOperations(operationIds: readonly string[]): Promise<void>;
  /** A value this device keeps for the account outside the Dataset, like the Google Calendar cache. */
  getValue(key: string): Promise<unknown>;
  setValue(key: string, value: unknown): Promise<void>;
}

export interface ApplyBatchResult {
  dataset: Dataset;
  accepted: SyncOperation[];
  changedEntities: Array<{ type: SyncOperation["entityType"]; id: string }>;
}

/** Applies a batch to a Dataset, reporting which operations were accepted and what changed. */
export function applyBatch(dataset: Dataset, operations: readonly SyncOperation[]): ApplyBatchResult {
  let next = dataset;
  const accepted: SyncOperation[] = [];
  const changed = new Map<string, { type: SyncOperation["entityType"]; id: string }>();
  for (const operation of operations) {
    const result = applyOperation(next, operation);
    if (result.rejected) continue;
    accepted.push(operation);
    if (result.changed) {
      next = result.dataset;
      changed.set(`${operation.entityType}:${operation.entityId}`, { type: operation.entityType, id: operation.entityId });
    }
  }
  return { dataset: next, accepted, changedEntities: [...changed.values()] };
}

/** In-memory store, used in tests and as the reference behaviour for persistent adapters. */
export class MemoryLocalStore implements LocalStore {
  private dataset: Dataset = emptyDataset();
  private outbox: SyncOperation[] = [];
  private cursor = 0;
  private readonly values = new Map<string, unknown>();
  private readonly listeners = new Set<(dataset: Dataset) => void>();

  constructor(private readonly deviceId = `device_${crypto.randomUUID()}`) {}

  async initialize(): Promise<void> {}
  async close(): Promise<void> { this.listeners.clear(); }
  async getDeviceId(): Promise<string> { return this.deviceId; }
  async load(): Promise<Dataset> { return this.dataset; }

  async apply(operations: readonly SyncOperation[]): Promise<Dataset> {
    const result = applyBatch(this.dataset, operations);
    this.dataset = result.dataset;
    this.outbox.push(...result.accepted);
    for (const listener of this.listeners) listener(this.dataset);
    return this.dataset;
  }

  async applyRemote(operations: readonly SyncOperation[]): Promise<Dataset> {
    this.dataset = applyBatch(this.dataset, operations).dataset;
    for (const listener of this.listeners) listener(this.dataset);
    return this.dataset;
  }

  async getCursor(): Promise<number> { return this.cursor; }
  async setCursor(cursor: number): Promise<void> { this.cursor = cursor; }

  subscribe(listener: (dataset: Dataset) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async getOutbox(limit = 500): Promise<SyncOperation[]> { return this.outbox.slice(0, limit); }
  async getPendingOperationCount(): Promise<number> { return this.outbox.length; }
  async acknowledgeOperations(operationIds: readonly string[]): Promise<void> {
    const done = new Set(operationIds);
    this.outbox = this.outbox.filter((operation) => !done.has(operation.opId));
  }
  async getValue(key: string): Promise<unknown> { return this.values.get(key); }
  async setValue(key: string, value: unknown): Promise<void> { this.values.set(key, structuredClone(value)); }
}
